import { rackWidthM } from '@eombor/contracts';

/**
 * Ombor rejasining tekislik geometriyasi (metrda, X-Y pol tekisligi).
 *
 * Bu yerda KATAK geometriyasi yo'q: katakning o'rni faqat
 * `cellTransform()` (`@eombor/contracts`) dan olinadi. Bu fayl stellajning
 * pol ustidagi izini (footprint) va panjaraga yopishishni biladi — bular
 * konstruktorning o'z ishi va backendga yuborilmaydi.
 */

/**
 * Panjara qadami — 0.25 m.
 *
 * Nima uchun shu: europallet 0.8 × 1.2 m, javon oralig'i esa odatda
 * 25 santimetrning karrasi bilan o'lchanadi. Mayda qadam (1 sm) rejani
 * "titratadi" va ikkita javon 3 santimetr farq bilan qo'yilib qoladi.
 */
export const GRID_STEP = 0.25;

/** Qiymatni panjaraga yopishtiradi. Manfiy son ham to'g'ri yaxlitlanadi. */
export const snap = (value: number, step: number = GRID_STEP): number =>
  Math.round(value / step) * step;

/**
 * Millimetrgacha yaxlitlash.
 *
 * Chetlarni solishtirganda kerak: `0.1 + 0.2` kabi yig'indi 1e-16 farq
 * bilan chiqadi va ikkita javon "tegib turibdi" o'rniga "kesishyapti"
 * bo'lib qolardi. Millimetr — ombor uchun baribir eng mayda o'lcham.
 */
const round = (value: number): number => Math.round(value * 1000) / 1000;

export interface PlanRect {
  x: number;
  y: number;
  w: number;
  d: number;
}

export interface RackPlacement {
  pos: { x: number; y: number; rotationDeg: number };
  /** Proyomlar — umumiy uzunlik ularning yig'indisi. */
  /** HAR QATORNING proyomlari — rejada faqat yig'indisi (uzunlik) kerak. */
  bays: number[][];
  depthM: number;
}

/**
 * Burilgan stellajning pol ustidagi to'g'ri burchakli chegarasi (AABB).
 * `pos` — stellaj MARKAZI, `cellTransform` bilan bir xil kelishuv.
 */
export const rackAabb = (rack: RackPlacement): PlanRect => {
  const rad = (rack.pos.rotationDeg * Math.PI) / 180;
  const halfW = rackWidthM(rack) / 2;
  const halfD = rack.depthM / 2;
  const extentX = Math.abs(halfW * Math.cos(rad)) + Math.abs(halfD * Math.sin(rad));
  const extentY = Math.abs(halfW * Math.sin(rad)) + Math.abs(halfD * Math.cos(rad));

  return { x: rack.pos.x - extentX, y: rack.pos.y - extentY, w: extentX * 2, d: extentY * 2 };
};

export interface WarehouseFloor {
  lengthM: number;
  widthM: number;
}

/**
 * Stellaj ombor devorlari ichidami.
 *
 * Chegaradan chiqqan javonni saqlashga ruxsat berilmaydi va bu **qattiq**
 * qoida: 3D sahnada devor ichida turgan javon bilan haqiqiy ombor bir-biriga
 * to'g'ri kelmasa, butun ko'rinishga ishonch yo'qoladi.
 */
export const withinFloor = (rack: RackPlacement, floor: WarehouseFloor): boolean => {
  const box = rackAabb(rack);
  // Suzuvchi nuqta xatosi uchun kichik bag'rikenglik (0.1 mm).
  const eps = 1e-4;
  return (
    box.x >= -eps &&
    box.y >= -eps &&
    box.x + box.w <= floor.lengthM + eps &&
    box.y + box.d <= floor.widthM + eps
  );
};

/** Zona to'rtburchagi ombor ichidami. */
export const rectWithinFloor = (rect: PlanRect, floor: WarehouseFloor): boolean => {
  const eps = 1e-4;
  return (
    rect.x >= -eps &&
    rect.y >= -eps &&
    rect.w > 0 &&
    rect.d > 0 &&
    rect.x + rect.w <= floor.lengthM + eps &&
    rect.y + rect.d <= floor.widthM + eps
  );
};

/**
 * Ikki to'rtburchak kesishadimi — chetlari tegib turishi kesishish EMAS.
 *
 * 0,1 mm bag'rikenglik shart: `1.95 - 0.65` kabi ayirma 1,3 dan bir necha
 * yuz kvintillion ulushga kichik chiqadi va yonma-yon turgan ikki javon
 * "kesishyapti" deb topilardi — natijada dastur ularni bir-biridan
 * uzoqlashtirib, orada tirqish qoldirardi.
 */
export const rectsOverlap = (a: PlanRect, b: PlanRect): boolean => {
  const eps = 1e-4;
  return (
    a.x < b.x + b.w - eps &&
    b.x < a.x + a.w - eps &&
    a.y < b.y + b.d - eps &&
    b.y < a.y + a.d - eps
  );
};

/**
 * Joy izlanadigan koordinatalar — panjara qadamlari VA MAVJUD
 * TO'RTBURCHAKLARNING CHETLARI.
 *
 * Faqat panjara bo'lgani uchun javonlar orasida bo'sh joy qolib ketardi:
 * 1,3 m li javon 0 dan boshlansa, keyingi panjara qadami 1,5 m da turadi
 * va ikkisi orasida 20 santimetrlik hech kimga keraksiz tirqish paydo
 * bo'lardi. Real omborda javonlar bir-biriga TEGIB turadi — shuning uchun
 * mavjud javonning o'ng cheti ham nomzod bo'ladi.
 *
 * Ro'yxat o'sish tartibida: qidiruv chapdan o'ngga, yuqoridan pastga
 * boradi va yangi javon oldingisining yoniga tushadi.
 */
const stops = (from: number, to: number, edges: readonly number[]): number[] => {
  if (to < from - 1e-4) return [];
  const values = new Set<number>([round(from)]);
  for (let value = from + GRID_STEP; value <= to + 1e-4; value += GRID_STEP) {
    values.add(round(value));
  }
  for (const edge of edges) {
    if (edge > from + 1e-4 && edge <= to + 1e-4) values.add(round(edge));
  }
  return [...values].sort((a, b) => a - b);
};

/**
 * To'rtburchakka ombor ichidan bo'sh joy topadi — zona uchun.
 *
 * Zona ham javon kabi qo'lda joylashtirilmaydi: odam o'lchamni aytadi,
 * joyni dastur topadi. Mavjud zonalar bilan kesishmaydi, chunki bir
 * kvadrat metr ikki zonaga tegishli bo'lishi mumkin emas.
 */
export const placeRect = (
  size: { w: number; d: number },
  taken: readonly PlanRect[],
  floor: WarehouseFloor,
): { x: number; y: number } | null => {
  const xs = stops(0, floor.lengthM - size.w, taken.map((rect) => round(rect.x + rect.w)));
  const ys = stops(0, floor.widthM - size.d, taken.map((rect) => round(rect.y + rect.d)));

  for (const y of ys) {
    for (const x of xs) {
      const candidate = { x, y, w: size.w, d: size.d };
      if (!rectWithinFloor(candidate, floor)) continue;
      if (taken.some((other) => rectsOverlap(other, candidate))) continue;
      return { x, y };
    }
  }
  return null;
};

/**
 * Stellaj O'Z ZONASI ichidami.
 *
 * Chiqib ketgan javon rad ETILMAYDI — u allaqachon shunday qurilgan
 * bo'lishi mumkin (eski ma'lumot) va uni jimgina ko'chirib qo'yish
 * etiketkalar bilan haqiqiy javon orasidagi bog'lanishni buzardi. O'rniga
 * rejada belgilanadi: odam ko'radi va o'zi hal qiladi.
 */
export const rackWithinZone = (rack: RackPlacement, zone: PlanRect): boolean => {
  const box = rackAabb(rack);
  const eps = 1e-4;
  return (
    box.x >= zone.x - eps &&
    box.y >= zone.y - eps &&
    box.x + box.w <= zone.x + zone.w + eps &&
    box.y + box.d <= zone.y + zone.d + eps
  );
};

/**
 * Sudralayotgan javonni panjaraga yopishtiradi — MARKAZINI emas, CHETINI.
 *
 * Markazni yaxlitlash javonni panjaradan chiqarib yuborardi: eni 1,3 m
 * bo'lgan javonning markazi 0,25 ga karrali bo'lsa, cheti 0,65 ga siljib
 * qoladi va u na zonaning chetiga, na qo'shnisiga tegadi. Odam esa
 * javonning MARKAZINI emas, chetini boshqa narsaga tekislaydi.
 */
export const snapRackPos = (rack: RackPlacement): { x: number; y: number } => {
  const box = rackAabb(rack);
  return {
    x: round(rack.pos.x + (snap(box.x) - box.x)),
    y: round(rack.pos.y + (snap(box.y) - box.y)),
  };
};

/**
 * Yangi stellajga ZONA ICHIDAN bo'sh joy topadi.
 *
 * Odam javonni qayerga qo'yishni oldindan o'ylab o'tirmasligi kerak: u
 * o'lchamni kiritadi va zonani tanlaydi, joyni dastur topadi. Topolmasa
 * `null` qaytadi va bu ochiq aytiladi — javonni jimgina boshqa zonaga yoki
 * mavjudining ustiga qo'yib yuborish eng yomon variant.
 *
 * Javon zonaning CHETIGA va qo'shnisiga TEGIB turadi: nomzod
 * koordinatalar `stops` dan olinadi, markaz esa aniq hisoblanadi va
 * yaxlitlanmaydi — yaxlitlash uni yana tirqish bilan qoldirardi.
 */
export const placeInZone = (
  zone: PlanRect,
  rack: Pick<RackPlacement, 'bays' | 'depthM'>,
  taken: readonly RackPlacement[],
  floor: WarehouseFloor,
  /** Javon bo'yiga (0°) yoki eniga (90°) turadimi. */
  rotationDeg = 0,
): { x: number; y: number } | null => {
  const boxes = taken.map(rackAabb);
  /* Burilgan javonning polda egallagan izi boshqacha: eni bilan chuqurligi
     o'rin almashadi. Shuning uchun qadamlar `rackAabb` dan olinadi, `bays`
     yig'indisidan emas. */
  const probe = { pos: { x: 0, y: 0, rotationDeg }, bays: rack.bays, depthM: rack.depthM };
  const footprint = rackAabb(probe);

  const xs = stops(zone.x, zone.x + zone.w - footprint.w, boxes.map((box) => round(box.x + box.w)));
  const ys = stops(zone.y, zone.y + zone.d - footprint.d, boxes.map((box) => round(box.y + box.d)));

  for (const y of ys) {
    for (const x of xs) {
      // `pos` — stellaj MARKAZI, chegara esa chap-yuqori burchakdan sanaladi.
      const centre = { x: round(x + footprint.w / 2), y: round(y + footprint.d / 2) };
      const candidate = { ...probe, pos: { ...centre, rotationDeg } };
      if (!withinFloor(candidate, floor)) continue;
      if (!rackWithinZone(candidate, zone)) continue;
      if (boxes.some((box) => rectsOverlap(box, rackAabb(candidate)))) continue;
      return centre;
    }
  }
  return null;
};
